import { useMemo, useState } from "react";
import { AdaptiveGrid, Page, PageHeader } from "../adaptive/primitives";
import { DEMO_DEFINITIONS } from "./definitions";

export interface DemoOrganizationOption {
  readonly slug: string;
  readonly displayName: string;
}

function statusTone(status: string): "success" | "warning" | "neutral" {
  if (status === "reference") return "success";
  if (status === "building") return "warning";
  return "neutral";
}

export function DemosWorkspace({
  organizations: _organizations,
  navigate,
}: {
  organizations: readonly DemoOrganizationOption[];
  navigate: (path: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("");

  const industries = useMemo(
    () => Array.from(new Map(
      DEMO_DEFINITIONS.map(({ manifest }) => [
        manifest.industryKey,
        manifest.industryLabel,
      ]),
    ).entries()).sort((left, right) => left[1].localeCompare(right[1])),
    [],
  );

  const demos = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return DEMO_DEFINITIONS.filter(({ manifest }) => {
      if (industry && manifest.industryKey !== industry) return false;
      if (!query) return true;
      return [
        manifest.name,
        manifest.industryLabel,
        manifest.nicheLabel,
        manifest.solutionLabel,
        manifest.summary,
      ].some((value) => value.toLocaleLowerCase().includes(query));
    });
  }, [industry, search]);

  return <Page>
    <PageHeader
      eyebrow="ClickDialog interno"
      title="Demos"
      description="Catálogo interno de demos operativas. Elegí una, abrila y usala para mostrar una solución."
    />

    <section className="details-card" aria-labelledby="demo-catalog-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Catálogo</p>
          <h2 id="demo-catalog-title">Elegir una demo</h2>
          <p>{demos.length} demos visibles.</p>
        </div>
      </div>

      <AdaptiveGrid compact={1} medium={2} wide={2}>
        <label htmlFor="demo-search">Buscar
          <input
            id="demo-search"
            type="search"
            placeholder="Nombre, industria o solución…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label htmlFor="demo-industry">Industria
          <select
            id="demo-industry"
            value={industry}
            onChange={(event) => setIndustry(event.target.value)}
          >
            <option value="">Todas</option>
            {industries.map(([key, label]) =>
              <option key={key} value={key}>{label}</option>
            )}
          </select>
        </label>
      </AdaptiveGrid>
    </section>

    <AdaptiveGrid className="demo-catalog-grid" compact={1} medium={2} wide={2}>
      {demos.map((definition) => {
        const demo = definition.manifest;
        return <article className="demo-catalog-card" key={demo.id}>
          <div className="demo-catalog-card__top">
            <div>
              <p className="eyebrow">{demo.industryLabel} · {demo.nicheLabel}</p>
              <h3>{demo.name}</h3>
            </div>
            <span className={`status-pill ${statusTone(demo.status)}`}>{demo.statusLabel}</span>
          </div>

          <p className="demo-catalog-card__summary">{demo.summary}</p>

          <div className="demo-module-chips" aria-label="Módulos incluidos">
            {definition.modules.map((module) =>
              <span key={module.key}>{module.label}</span>
            )}
          </div>

          <div className="demo-catalog-card__footer">
            <span>{demo.solutionLabel}</span>
            <button
              type="button"
              className="primary-button"
              onClick={() => navigate(`/demos/${encodeURIComponent(demo.id)}`)}
            >
              {demo.launch.kind === "unavailable" ? "Ver alcance" : "Abrir demo"}
            </button>
          </div>
        </article>;
      })}
    </AdaptiveGrid>

    {demos.length === 0 && <section className="details-card">
      <h2>Sin resultados</h2>
      <p className="muted">No hay demos que coincidan con la búsqueda.</p>
    </section>}
  </Page>;
}
